/**
 * The modal-surface store — which generative-UI surface (settings / builder /
 * billing / referral) is currently open as a `Modal`, if any. The agent opens one
 * by emitting a `modal` {@link UiDirective} (→ `onOpenSurface`), and surface
 * chrome (the empty-state / "new companion" CTA) opens the builder directly. Thin
 * client state: a single active surface with the directive's `title`/`dismissible`
 * hints; no domain logic, no network.
 */
import type { UiSurface } from '@4f/shared';
import { createSignal } from 'solid-js';

/** The open surface plus the `modal` directive's presentation hints. */
export interface OpenSurface {
  readonly surface: UiSurface;
  /** Optional heading override from the directive. */
  readonly title?: string;
  /** Whether Escape / scrim click may close it (a required flow sets `false`). */
  readonly dismissible: boolean;
}

/** Options carried by a `modal` directive when opening a surface. */
export interface OpenSurfaceOptions {
  readonly title?: string;
  readonly dismissible?: boolean;
}

export interface SurfaceStore {
  /** The active surface, or `null` when none is open. */
  readonly active: () => OpenSurface | null;
  /** Open a surface (replaces any currently-open one). */
  open(surface: UiSurface, options?: OpenSurfaceOptions): void;
  close(): void;
}

export function createSurfaceStore(): SurfaceStore {
  const [active, setActive] = createSignal<OpenSurface | null>(null);
  return {
    active,
    open(surface, options) {
      setActive({
        surface,
        title: options?.title,
        dismissible: options?.dismissible ?? true,
      });
    },
    close() {
      setActive(null);
    },
  };
}
